import React, { useState, useEffect } from 'react';
import { Upload, X, Type, Check, AlertCircle, Trash2 } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

interface CustomFont {
  id: string;
  name: string;
  family: string;
  url: string;
  format: 'woff' | 'woff2' | 'ttf' | 'otf';
  size: number;
  uploadedAt: Date;
}

interface FontManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onFontAdded: (font: CustomFont) => void;
  customFonts: CustomFont[];
  onDeleteFont: (fontId: string) => void;
}

export const FontManager: React.FC<FontManagerProps> = ({
  isOpen,
  onClose,
  onFontAdded,
  customFonts,
  onDeleteFont
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { showSuccess, showError, showWarning } = useToast();

  if (!isOpen) return null;

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    const allowedTypes = ['.woff', '.woff2', '.ttf', '.otf'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      showError('Invalid File Type', 'Please upload a valid font file (.woff, .woff2, .ttf, or .otf)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      showError('File Too Large', 'Font files must be smaller than 5MB');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      // Convert file to data URL
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Extract font name (remove extension)
      const fontName = file.name.replace(/\.[^/.]+$/, '');
      const fontFamily = `custom-${fontName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

      // Create custom font object
      const customFont: CustomFont = {
        id: `font-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: fontName,
        family: fontFamily,
        url: dataUrl,
        format: fileExtension.slice(1) as CustomFont['format'],
        size: file.size,
        uploadedAt: new Date()
      };

      // Load the font
      await loadCustomFont(customFont);

      onFontAdded(customFont);
      showSuccess('Font Uploaded', `${fontName} has been added to your custom fonts`);

    } catch (error) {
      console.error('Font upload error:', error);
      showError('Upload Failed', 'Failed to upload font. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const loadCustomFont = async (font: CustomFont): Promise<void> => {
    return new Promise((resolve, reject) => {
      const fontFace = new FontFace(font.family, `url(${font.url})`);
      
      fontFace.load().then((loadedFont) => {
        document.fonts.add(loadedFont);
        resolve();
      }).catch((error) => {
        console.error('Font loading error:', error);
        reject(error);
      });
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDeleteFont = (fontId: string) => {
    const font = customFonts.find(f => f.id === fontId);
    if (font) {
      // Remove from document fonts
      const fontFaces = Array.from(document.fonts);
      const fontFace = fontFaces.find(f => f.family === font.family);
      if (fontFace) {
        document.fonts.delete(fontFace);
      }
      
      onDeleteFont(fontId);
      showWarning('Font Removed', `${font.name} has been removed from your custom fonts`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Type className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Font Manager</h2>
              <p className="text-sm text-gray-600">Upload and manage custom fonts for your banners</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isUploading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Upload Area */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Upload New Font</h3>
            
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging
                  ? 'border-purple-400 bg-purple-50'
                  : 'border-gray-300 hover:border-gray-400'
              } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isUploading ? (
                <div className="space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Uploading font...</p>
                    <div className="mt-2 bg-gray-200 rounded-full h-2 w-64 mx-auto">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{uploadProgress}%</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <Type className="mx-auto h-12 w-12 text-gray-400" />
                  <div>
                    <p className="text-lg font-medium text-gray-900">
                      Drop font files here or click to browse
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Supports .woff, .woff2, .ttf, and .otf files up to 5MB
                    </p>
                  </div>
                  <label className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer transition-colors">
                    <Upload size={16} />
                    <span>Choose Files</span>
                    <input
                      type="file"
                      accept=".woff,.woff2,.ttf,.otf"
                      onChange={(e) => handleFileUpload(e.target.files)}
                      className="hidden"
                      multiple={false}
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Font Guidelines */}
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Font Upload Guidelines</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <div>• <strong>Supported formats:</strong> WOFF2 (recommended), WOFF, TTF, OTF</div>
                <div>• <strong>File size limit:</strong> 5MB per font file</div>
                <div>• <strong>Best practices:</strong> Use WOFF2 for better compression and loading speed</div>
                <div>• <strong>Licensing:</strong> Ensure you have proper licensing for commercial use</div>
                <div>• <strong>Performance:</strong> Limit custom fonts to maintain fast loading times</div>
              </div>
            </div>
          </div>

          {/* Custom Fonts List */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Your Custom Fonts</h3>
              <span className="text-sm text-gray-500">
                {customFonts.length} font{customFonts.length !== 1 ? 's' : ''} uploaded
              </span>
            </div>

            {customFonts.length > 0 ? (
              <div className="space-y-3">
                {customFonts.map((font) => (
                  <div
                    key={font.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Type className="h-5 w-5 text-purple-600" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-1">
                          <h4 className="font-medium text-gray-900">{font.name}</h4>
                          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded uppercase">
                            {font.format}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>Family: {font.family}</span>
                          <span>Size: {formatFileSize(font.size)}</span>
                          <span>Added: {font.uploadedAt.toLocaleDateString()}</span>
                        </div>
                      </div>

                      {/* Font Preview */}
                      <div className="text-right">
                        <div
                          className="text-lg text-gray-900 mb-1"
                          style={{ fontFamily: font.family }}
                        >
                          The quick brown fox
                        </div>
                        <div
                          className="text-sm text-gray-600"
                          style={{ fontFamily: font.family }}
                        >
                          jumps over the lazy dog
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <div className="flex items-center text-green-600">
                        <Check size={16} />
                        <span className="text-xs ml-1">Loaded</span>
                      </div>
                      
                      <button
                        onClick={() => handleDeleteFont(font.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Font"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Type className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <p>No custom fonts uploaded yet</p>
                <p className="text-sm mt-1">Upload your first font to get started</p>
              </div>
            )}
          </div>

          {/* Font Usage Tips */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-900 mb-1">Font Usage Tips</h4>
                <div className="text-sm text-yellow-800 space-y-1">
                  <div>• Custom fonts will be available in the font dropdown when editing text elements</div>
                  <div>• Fonts are stored locally and will persist across browser sessions</div>
                  <div>• When exporting banners, custom fonts will be embedded in the HTML output</div>
                  <div>• For production use, ensure fonts are properly licensed for web use</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            Custom fonts are stored locally in your browser
          </div>
          
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};